import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { RunningStats, StravaAthlete } from '../types/strava';
import {
  clearTokens,
  exchangeCodeForToken,
  getAthleteInfo,
  getAuthUrl,
  getRunningStats,
  isAuthenticated as isAuthenticatedStrava,
} from '../services/stravaService';

export interface UseStravaReturn {
  isAuthenticated: boolean;
  athleteInfo: StravaAthlete | null;
  login: () => void;
  loadRunningStats: (year: number) => Promise<RunningStats>;
  loadAthleteInfo: () => Promise<void>;
}

export const useStrava = (): UseStravaReturn => {
  const navigate = useNavigate();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [athleteInfo, setAthleteInfo] = useState<StravaAthlete | null>(null);

  useEffect(() => {
    setIsAuthenticated(isAuthenticatedStrava());
  }, []);

  // Handle OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const errorParam = urlParams.get('error');

    if (errorParam) {
      console.error('Authentication failed');
      return;
    }

    if (code && !isAuthenticated) {
      handleOAuthCallback(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleOAuthCallback = async (code: string): Promise<void> => {
    try {
      await exchangeCodeForToken(code);
      setIsAuthenticated(true);
      navigate(window.location.pathname, { replace: true });
    } catch (err) {
      console.error('Failed to authenticate with Strava:', err);
    }
  };

  const login = (): void => {
    window.location.href = getAuthUrl();
  };

  const logout = (): void => {
    clearTokens();
    setIsAuthenticated(false);
    setAthleteInfo(null);
  };

  const loadRunningStats = useCallback(
    async (year: number): Promise<RunningStats> => {
      if (!isAuthenticated) {
        throw new Error('Not authenticated');
      }

      try {
        const stats = await getRunningStats(year);
        console.log('Loaded running stats:', stats);
        console.log('Individual activities since start of year:', year, stats.activities);
        return stats;
      } catch (err) {
        console.error('Error loading running stats:', err);

        // If unauthorized, clear tokens and require re-authentication
        if (err instanceof Error && (err.message.includes('401') || err.message.includes('unauthorized'))) {
          logout();
        }
        throw err;
      }
    },
    [isAuthenticated]
  );

  const loadAthleteInfo = useCallback(async (): Promise<void> => {
    if (!isAuthenticated) {
      return;
    }

    try {
      const info = await getAthleteInfo();

      const expectedAthleteId = process.env.REACT_APP_STRAVA_ATHLETE_ID;
      if (expectedAthleteId && info.id.toString() !== expectedAthleteId) {
        console.error('Athlete ID mismatch: expected', expectedAthleteId, 'but got', info.id);
        logout();
        throw new Error('Authenticated athlete does not match expected athlete ID');
      }

      setAthleteInfo(info);
    } catch (err) {
      console.error('Error loading athlete info:', err);

      // If unauthorized, clear tokens and require re-authentication
      if (err instanceof Error && (err.message.includes('401') || err.message.includes('unauthorized'))) {
        logout();
      }
    }
  }, [isAuthenticated]);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadAthleteInfo();
    }
  }, [isAuthenticated, loadAthleteInfo]);

  return {
    login,
    isAuthenticated,
    athleteInfo,
    loadRunningStats,
    loadAthleteInfo,
  };
};
